import React, { useReducer, useEffect, useRef, useCallback, useMemo, useState } from 'react';
import { useShallow } from 'zustand/shallow';
import { useAmlStore } from '../../stores/amlStore';
import { amlApi } from '../../services/amlApi';
import type { AmlTransaction, AmlLocation } from '../../types/aml';
import { Select, DatePicker } from '../../../../components/ui';
import { Button, TextField, TextArea, SlidePanel, Text, NumericStepper, Alert } from '../../../../components/ui';
import { useToast } from '../../../../components/feedback';
import { useTranslation } from '../../../../hooks';
import { useUIStore } from '../../../../stores/uiStore';
import { formatDate, formatMoney, normalizeNumberInput } from '../shared';

interface TransactionFormProps {
  transaction: AmlTransaction | null;
  onClose: () => void;
  readOnly?: boolean;
}

// ── Form state ──

interface FormState {
  aml_customer_id: string;
  aml_location_id: string;
  transaction_date: string;
  inbound: string;
  outbound: string;
  amount_collected: string;
  fee_inbound: string;
  fee_outbound: string;
  fee_collected: string;
  invoice_number: string;
  invoice_date: string;
  invoice_amount: string;
  stops: string;
  pcs: string;
  notes: string;
}

const EMPTY_FORM: FormState = {
  aml_customer_id: '',
  aml_location_id: '',
  transaction_date: '',
  inbound: '',
  outbound: '',
  amount_collected: '',
  fee_inbound: '',
  fee_outbound: '',
  fee_collected: '',
  invoice_number: '',
  invoice_date: '',
  invoice_amount: '',
  stops: '',
  pcs: '',
  notes: '',
};

type FormAction =
  | { type: 'SET_FIELD'; field: keyof FormState; value: string }
  | { type: 'RESET'; state: FormState };

function formReducer(state: FormState, action: FormAction): FormState {
  switch (action.type) {
    case 'SET_FIELD':
      return state[action.field] === action.value ? state : { ...state, [action.field]: action.value };
    case 'RESET':
      return action.state;
    default:
      return state;
  }
}

function transactionToForm(t: AmlTransaction): FormState {
  return {
    aml_customer_id: t.aml_customer_id,
    aml_location_id: t.aml_location_id,
    transaction_date: t.transaction_date ? t.transaction_date.slice(0, 10) : '',
    inbound: t.inbound ?? '',
    outbound: t.outbound ?? '',
    amount_collected: t.amount_collected ?? '',
    fee_inbound: t.fee_inbound ?? '',
    fee_outbound: t.fee_outbound ?? '',
    fee_collected: t.fee_collected ?? '',
    invoice_number: t.invoice_number ?? '',
    invoice_date: t.invoice_date ? t.invoice_date.slice(0, 10) : '',
    invoice_amount: t.invoice_amount ?? '',
    stops: t.stops != null ? String(t.stops) : '',
    pcs: t.pcs != null ? String(t.pcs) : '',
    notes: t.notes ?? '',
  };
}

// ── Sub-components ──

const DetailRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex flex-col gap-1">
    <Text variant="label">{label}</Text>
    <span className="text-sm text-content">{value ?? '—'}</span>
  </div>
);

// ── Main component ──

const TransactionForm: React.FC<TransactionFormProps> = ({ transaction, onClose, readOnly = false }) => {
  const {
    customersForFilter,
    fetchCustomersForFilter,
    createTransaction,
    updateTransaction,
  } = useAmlStore(
    useShallow((s) => ({
      customersForFilter: s.customersForFilter,
      fetchCustomersForFilter: s.fetchCustomersForFilter,
      createTransaction: s.createTransaction,
      updateTransaction: s.updateTransaction,
    })),
  );
  const { success, error: toastError } = useToast();
  const { language } = useUIStore();
  const { t: tr } = useTranslation(language);

  const [form, dispatch] = useReducer(formReducer, EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [localLocations, setLocalLocations] = useState<AmlLocation[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const initialLocationId = useRef<string>('');

  const setField = useCallback(
    (field: keyof FormState, value: string) => dispatch({ type: 'SET_FIELD', field, value }),
    [],
  );

  const isEdit = !!transaction && !readOnly;

  useEffect(() => {
    if (!readOnly) fetchCustomersForFilter(true);
  }, [readOnly, fetchCustomersForFilter]);

  useEffect(() => {
    if (transaction) {
      initialLocationId.current = transaction.aml_location_id;
      dispatch({ type: 'RESET', state: transactionToForm(transaction) });
    } else {
      dispatch({ type: 'RESET', state: EMPTY_FORM });
    }
  }, [transaction]);

  useEffect(() => {
    if (readOnly) return;
    if (!form.aml_customer_id) {
      setLocalLocations([]);
      setField('aml_location_id', '');
      return;
    }
    let cancelled = false;
    setLocationsLoading(true);
    setLocalLocations([]);
    amlApi.listLocations({ customer_id: form.aml_customer_id }).then((list) => {
      if (cancelled) return;
      setLocalLocations(list);
      dispatch({
        type: 'SET_FIELD',
        field: 'aml_location_id',
        value: (() => {
          if (form.aml_location_id && list.some((l) => l.id === form.aml_location_id)) return form.aml_location_id;
          if (initialLocationId.current && list.some((l) => l.id === initialLocationId.current)) return initialLocationId.current;
          return '';
        })(),
      });
    }).catch(() => {
      if (!cancelled) setLocalLocations([]);
    }).finally(() => {
      if (!cancelled) setLocationsLoading(false);
    });
    return () => { cancelled = true; };
  }, [form.aml_customer_id, readOnly, setField]); // eslint-disable-line react-hooks/exhaustive-deps

  const parseNum = useCallback((s: string): number | null => {
    if (!s.trim()) return null;
    const normalized = normalizeNumberInput(s);
    if (normalized == null) return null;
    const n = parseFloat(normalized);
    return Number.isNaN(n) ? null : n;
  }, []);

  const selectedCustomer = useMemo(
    () => customersForFilter.find((c) => c.id === form.aml_customer_id) ?? null,
    [customersForFilter, form.aml_customer_id],
  );
  const selectedCustomerBlocked = selectedCustomer?.screening_status === 'potential_match';
  const selectedCustomerUnscreened = !!form.aml_customer_id && !selectedCustomerBlocked
    && selectedCustomer?.screening_status == null;

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.aml_customer_id || !form.aml_location_id) return;
    if (selectedCustomerBlocked) return;
    setSaving(true);
    try {
      const data = {
        aml_customer_id: form.aml_customer_id,
        aml_location_id: form.aml_location_id,
        transaction_date: form.transaction_date || null,
        inbound: parseNum(form.inbound),
        outbound: parseNum(form.outbound),
        amount_collected: parseNum(form.amount_collected),
        fee_inbound: parseNum(form.fee_inbound),
        fee_outbound: parseNum(form.fee_outbound),
        fee_collected: parseNum(form.fee_collected),
        invoice_number: form.invoice_number.trim() || null,
        invoice_date: form.invoice_date || null,
        invoice_amount: parseNum(form.invoice_amount),
        stops: parseNum(form.stops) != null ? Math.floor(Number(parseNum(form.stops))) : null,
        pcs: parseNum(form.pcs) != null ? Math.floor(Number(parseNum(form.pcs))) : null,
        notes: form.notes.trim() || null,
      };
      if (isEdit && transaction) {
        await updateTransaction(transaction.id, data);
        success(tr('amlTransactionToastUpdatedSuccessfully'));
      } else {
        await createTransaction(data);
        success(tr('amlTransactionToastCreatedSuccessfully'));
      }
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data?.detail
        ? (Array.isArray(err.response.data.detail)
            ? err.response.data.detail.map((d: any) => d.msg || d.message || JSON.stringify(d)).join('; ')
            : err.response.data.detail)
        : tr(isEdit ? 'amlTransactionToastFailedToUpdate' : 'amlTransactionToastFailedToCreate');
      toastError(msg);
    } finally {
      setSaving(false);
    }
  }, [form, isEdit, transaction, parseNum, updateTransaction, createTransaction, success, toastError, tr, onClose, selectedCustomerBlocked]);

  const customerOptions = useMemo(
    () => customersForFilter.map((c) => ({
      value: c.id,
      label: c.name,
      disabled: c.screening_status === 'potential_match',
      hint: c.screening_status === 'potential_match'
        ? tr('amlScreeningResultPotentialMatch')
        : c.screening_status == null
          ? tr('amlScreeningResultNotScreened')
          : undefined,
    })),
    [customersForFilter, tr],
  );

  const locationOptions = useMemo(
    () => localLocations.map((l) => ({ value: l.id, label: l.name })),
    [localLocations],
  );

  const title = readOnly
    ? tr('amlTransactionDetailTitle')
    : isEdit
    ? tr('amlTransactionEditTitle')
    : tr('amlTransactionAddTitle');

  return (
    <SlidePanel isOpen={true} onClose={onClose} title={title}>
      {readOnly && transaction ? (
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <DetailRow label={tr('amlTransactionLabelCustomer')} value={transaction.customer_name} />
            <DetailRow label={tr('amlTransactionLabelLocation')} value={transaction.location_name} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <DetailRow
              label={tr('amlTransactionLabelTransactionDate')}
              value={formatDate(transaction.transaction_date)}
            />
            <DetailRow
              label={tr('amlColInvoiceDate')}
              value={formatDate(transaction.invoice_date)}
            />
          </div>
          <div className="border-t border-subtle pt-4">
            <Text variant="label" className="text-content-muted mb-3 block">{tr('amlTransactionLabelAmounts')}</Text>
            <div className="grid grid-cols-3 gap-4">
              <DetailRow label={tr('amlColInbound')} value={formatMoney(transaction.inbound)} />
              <DetailRow label={tr('amlColOutbound')} value={formatMoney(transaction.outbound)} />
              <DetailRow label={tr('amlTransactionLabelAmountCollectedInOut')} value={formatMoney(transaction.amount_collected)} />
            </div>
          </div>
          <div className="border-t border-subtle pt-4">
            <Text variant="label" className="text-content-muted mb-3 block">{tr('amlTransactionLabelFees')}</Text>
            <div className="grid grid-cols-3 gap-4">
              <DetailRow label={tr('amlColFeeInbound')} value={formatMoney(transaction.fee_inbound)} />
              <DetailRow label={tr('amlColFeeOutbound')} value={formatMoney(transaction.fee_outbound)} />
              <DetailRow label={tr('amlTransactionLabelFeeCollectedInOut')} value={formatMoney(transaction.fee_collected)} />
            </div>
          </div>
          <div className="border-t border-subtle pt-4">
            <Text variant="label" className="text-content-muted mb-3 block">{tr('amlTransactionLabelInvoice')}</Text>
            <div className="grid grid-cols-3 gap-4">
              <DetailRow label={tr('amlColInvoiceNumber')} value={transaction.invoice_number} />
              <DetailRow
                label={tr('amlColInvoiceDate')}
                value={formatDate(transaction.invoice_date)}
              />
              <DetailRow label={tr('amlColInvoiceAmount')} value={formatMoney(transaction.invoice_amount)} />
            </div>
          </div>
          <div className="border-t border-subtle pt-4">
            <div className="grid grid-cols-2 gap-4">
              <DetailRow label={tr('amlColStops')} value={transaction.stops ?? '—'} />
              <DetailRow label={tr('amlColPcs')} value={transaction.pcs ?? '—'} />
            </div>
          </div>
          {transaction.notes && (
            <div className="border-t border-subtle pt-4">
              <DetailRow label={tr('amlColNotes')} value={transaction.notes} />
            </div>
          )}
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div>
            <Text variant="label" className="block mb-1.5">{tr('amlTransactionLabelCustomer')}</Text>
            <Select
              value={form.aml_customer_id}
              onChange={(v) => setField('aml_customer_id', v)}
              options={[{ value: '', label: tr('amlTransactionLabelSelectCustomer') }, ...customerOptions]}
              placeholder={tr('amlTransactionLabelSelectCustomer')}
              searchable
            />
            {selectedCustomerBlocked ? (
              <Alert variant="warning" className="mt-2 !p-3" title={tr('amlTransactionOfacBlockedTitle')}>
                <Text variant="caption" className="text-content-secondary">
                  {tr('amlTransactionOfacBlockedBody')}
                </Text>
              </Alert>
            ) : selectedCustomerUnscreened ? (
              <Alert variant="info" className="mt-2 !p-3" title={tr('amlTransactionOfacUnscreenedTitle')}>
                <Text variant="caption" className="text-content-secondary">
                  {tr('amlTransactionOfacUnscreenedBody')}
                </Text>
              </Alert>
            ) : null}
          </div>

          <div>
            <Text variant="label" className="block mb-1.5">{tr('amlTransactionLabelLocation')}</Text>
            <Select
              value={form.aml_location_id}
              onChange={(v) => setField('aml_location_id', v)}
              options={[{ value: '', label: locationsLoading ? tr('amlTransactionLabelLoading') : tr('amlTransactionLabelSelectLocation') }, ...locationOptions]}
              placeholder={tr('amlTransactionLabelSelectLocation')}
              disabled={!form.aml_customer_id || locationsLoading}
              searchable
            />
          </div>

          <div>
            <Text variant="label" className="block mb-1.5">{tr('amlTransactionLabelTransactionDate')}</Text>
            <DatePicker value={form.transaction_date} onChange={(v) => setField('transaction_date', v)} placeholder={tr('amlTransactionLabelSelectDate')} />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <NumericStepper label={tr('amlColInbound')} decimal value={form.inbound} onChange={(v) => setField('inbound', v)} placeholder="0" />
            <NumericStepper label={tr('amlColOutbound')} decimal value={form.outbound} onChange={(v) => setField('outbound', v)} placeholder="0" />
            <NumericStepper label={tr('amlTransactionLabelCollected')} decimal value={form.amount_collected} onChange={(v) => setField('amount_collected', v)} placeholder="0" />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <NumericStepper label={tr('amlColFeeInbound')} decimal value={form.fee_inbound} onChange={(v) => setField('fee_inbound', v)} placeholder="0" />
            <NumericStepper label={tr('amlColFeeOutbound')} decimal value={form.fee_outbound} onChange={(v) => setField('fee_outbound', v)} placeholder="0" />
            <NumericStepper label={tr('amlTransactionLabelFeeCollected')} decimal value={form.fee_collected} onChange={(v) => setField('fee_collected', v)} placeholder="0" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <TextField label={tr('amlColInvoiceNumber')} value={form.invoice_number} onChange={(e) => setField('invoice_number', e.target.value)} placeholder={tr('amlTransactionLabelInvoiceHash')} />
            <div>
              <Text variant="label" className="block mb-1.5">{tr('amlColInvoiceDate')}</Text>
              <DatePicker value={form.invoice_date} onChange={(v) => setField('invoice_date', v)} placeholder={tr('amlTransactionLabelSelectDate')} />
            </div>
          </div>

          <NumericStepper label={tr('amlColInvoiceAmount')} decimal value={form.invoice_amount} onChange={(v) => setField('invoice_amount', v)} placeholder="0" />

          <div className="grid grid-cols-2 gap-3">
            <NumericStepper label={tr('amlColStops')} value={form.stops} onChange={(v) => setField('stops', v)} placeholder="0" min={0} />
            <NumericStepper label={tr('amlColPcs')} value={form.pcs} onChange={(v) => setField('pcs', v)} placeholder="0" min={0} />
          </div>

          <TextArea label={tr('amlColNotes')} value={form.notes} onChange={(e) => setField('notes', e.target.value)} placeholder={tr('amlColNotes')} rows={3} />

          <div className="flex gap-3 pt-4 border-t border-subtle">
            <Button variant="ghost" size="lg" type="button" onClick={onClose}>
              {tr('amlBtnCancel')}
            </Button>
            <Button
              variant="primary"
              size="lg"
              type="submit"
              loading={saving}
              disabled={!form.aml_customer_id || !form.aml_location_id || selectedCustomerBlocked}
              className="flex-1"
            >
              {isEdit ? tr('amlBtnUpdateTransaction') : tr('amlBtnCreateTransaction')}
            </Button>
          </div>
        </form>
      )}
    </SlidePanel>
  );
};

export default TransactionForm;
